import { BadgeCheck, CalendarDays } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Img } from "./Reveal";
import { DOCTORS } from "@/data/content";
import { useBooking } from "@/lib/booking";

const LEAD_FIELDS = [
  ["Specialization", "specialization"],
  ["Experience", "experience"],
  ["Areas of Expertise", "expertise"],
  ["Professional Profile", "profile"],
];

export function Doctors() {
  const { openBooking } = useBooking();
  const lead = DOCTORS.lead;
  return (
    <section id="doctors" data-testid="doctors-section" className="py-24 lg:py-32 bg-cream/60">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionHeading
          chapter="06"
          overline="Our Doctors"
          title="Care Led by Qualified Hands"
          sub="Every Dyntra clinic is anchored by qualified practitioners. Meet the doctor behind our Sector 75, Noida clinic."
        />

        <Reveal className="mt-14">
          <div className="grid lg:grid-cols-5 overflow-hidden rounded-[2rem] bg-white border border-pine/10 shadow-lift">
            <div className="lg:col-span-2 relative">
              <Img src={lead.img} alt={lead.name} className="h-[420px] lg:h-[560px] w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-pine-deep/50 via-transparent to-transparent lg:bg-gradient-to-r" />
            </div>
            <div className="lg:col-span-3 p-8 md:p-12">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-soft/60 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-pine-deep">
                <BadgeCheck className="h-3.5 w-3.5 text-teal-brand" /> {lead.role}
              </span>
              <h3 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-pine-deep">{lead.name}</h3>
              <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {LEAD_FIELDS.map(([label, key]) => (
                  <div key={key} className={key === "profile" ? "sm:col-span-2" : ""}>
                    <dt className="text-[11px] uppercase tracking-[0.18em] font-bold text-teal-brand">{label}</dt>
                    <dd className="mt-1 text-sm text-slate-600">{lead[key]}</dd>
                  </div>
                ))}
              </dl>
              <button
                data-testid="doctor-book-cta"
                onClick={() => openBooking()}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-bold text-ivory shadow-lift transition-all duration-300 hover:bg-pine-deep hover:-translate-y-0.5"
              >
                <CalendarDays className="h-4 w-4" /> Book a Consultation
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {DOCTORS.others.map((d, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="group relative overflow-hidden rounded-3xl bg-white border border-pine/10 shadow-soft transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lift">
                <div className="overflow-hidden">
                  <Img src={d.img} alt={`${d.role} profile photo`} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <p className="font-display text-lg font-semibold text-pine-deep">{d.name}</p>
                  <p className="mt-0.5 text-sm font-semibold text-teal-brand">{d.role}</p>
                  <p className="mt-1 text-xs font-bold text-gold">{d.exp}</p>
                  <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-pine/50">{d.expertise}</p>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">{d.bio}</p>
                </div>
                <span className="absolute top-4 right-4 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-pine/70 border border-pine/10">
                  Demo Profile
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
